import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Avatar,
  Checkbox,
} from '@mui/material'
import { Edit, Trash2, ChevronDown, ChevronUp, Star, Heart, Share2, Copy } from 'lucide-react'
import { useSwipeable } from 'react-swipeable'
import { useMobile } from '../../../hooks/useMobile'
import { useHaptic } from '../../../hooks/useHaptic'
import { GESTURE_THRESHOLDS } from '../../../utils/gestureThresholds'
import type { MobileListCardProps } from './types'

/**
 * Map icon name to component
 */
const iconMap: Record<string, React.ComponentType<{ size?: string | number }>> = {
  Star,
  Heart,
  Share2,
  Copy,
}

export const MobileListCard: React.FC<MobileListCardProps> = ({
  primaryText,
  secondaryText,
  avatar,
  status,
  fields,
  onEdit,
  onDelete,
  onTap,
  collapsedFieldCount = 3,
  initialExpanded = false,
  onLongPress,
  secondaryActions,
  enableFullSwipe = false,
  selectionMode = false,
  isSelected = false,
  onSelectionChange,
}) => {
  const { isMobile } = useMobile()
  const { light, medium } = useHaptic()
  const [expanded, setExpanded] = useState(initialExpanded)
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [actionsRevealed, setActionsRevealed] = useState<'left' | 'right' | null>(null)

  // Long press detection
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null)
  const longPressFiredRef = useRef(false)

  /**
   * Calculate action button width based on actions available
   */
  const primaryActionWidth = (onEdit ? 40 : 0) + (onDelete ? 40 : 0) + 8 // 8 for gap
  const secondaryActionWidth = secondaryActions
    ? secondaryActions.length * 40 + (secondaryActions.length - 1) * 8
    : 0

  /**
   * Full swipe threshold (75% of card width, approx 250px)
   */
  const fullSwipeThreshold = 200

  /**
   * Clear long press timer
   */
  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])

  /**
   * Handle touch start for long press detection
   */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (selectionMode) return // Disable long press in selection mode

      const touch = e.touches[0]
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY }
      longPressFiredRef.current = false

      longPressTimerRef.current = setTimeout(() => {
        if (onLongPress) {
          medium() // Haptic feedback
          onLongPress()
          longPressFiredRef.current = true
        }
      }, GESTURE_THRESHOLDS.longPress.duration)
    },
    [onLongPress, medium, selectionMode]
  )

  /**
   * Handle touch move for long press cancellation
   */
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartPosRef.current) return

      const touch = e.touches[0]
      const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x)
      const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y)

      // Cancel if moved beyond threshold
      if (deltaX > GESTURE_THRESHOLDS.longPress.moveCancel || deltaY > GESTURE_THRESHOLDS.longPress.moveCancel) {
        clearLongPressTimer()
      }
    },
    [clearLongPressTimer]
  )

  /**
   * Handle touch end
   */
  const handleTouchEnd = useCallback(() => {
    clearLongPressTimer()
    touchStartPosRef.current = null
  }, [clearLongPressTimer])

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => clearLongPressTimer()
  }, [clearLongPressTimer])

  const swipeHandlers = useSwipeable({
    onSwipedLeft: (e) => {
      if (selectionMode) return // Disable swipe in selection mode

      // Check for full swipe auto-execute
      if (enableFullSwipe && Math.abs(e.deltaX) > fullSwipeThreshold && onDelete) {
        light()
        onDelete()
        setSwipeOffset(0)
        setActionsRevealed(null)
        return
      }

      if (e.deltaX < -50) {
        // If secondary actions were revealed, hide them
        if (actionsRevealed === 'right') {
          setActionsRevealed(null)
          setSwipeOffset(0)
        } else {
          // Reveal primary actions (edit/delete)
          setActionsRevealed('left')
          setSwipeOffset(-primaryActionWidth)
        }
      }
    },
    onSwipedRight: (e) => {
      if (selectionMode) return // Disable swipe in selection mode

      // Check for full swipe auto-execute
      if (enableFullSwipe && e.deltaX > fullSwipeThreshold && secondaryActions?.length) {
        light()
        secondaryActions[0].onClick()
        setSwipeOffset(0)
        setActionsRevealed(null)
        return
      }

      if (e.deltaX > 50) {
        // If primary actions were revealed, hide them
        if (actionsRevealed === 'left') {
          setActionsRevealed(null)
          setSwipeOffset(0)
        } else if (secondaryActions?.length) {
          // Reveal secondary actions
          setActionsRevealed('right')
          setSwipeOffset(secondaryActionWidth)
        }
      }
    },
    onSwiping: (e) => {
      if (selectionMode) return // Disable swipe in selection mode

      // Swipe left (reveal primary actions)
      if (e.deltaX < 0 && e.deltaX > -primaryActionWidth && actionsRevealed !== 'right') {
        setSwipeOffset(e.deltaX)
      }
      // Swipe right (reveal secondary actions or hide primary)
      else if (e.deltaX > 0) {
        if (actionsRevealed === 'left') {
          setSwipeOffset(-primaryActionWidth + e.deltaX)
        } else if (secondaryActions?.length && e.deltaX < secondaryActionWidth) {
          setSwipeOffset(e.deltaX)
        }
      }
    },
    trackTouch: true,
  })

  if (!isMobile) return null // Desktop uses table

  const handleCardTap = () => {
    // If long press fired, don't execute tap
    if (longPressFiredRef.current) return

    // In selection mode, toggle selection
    if (selectionMode && onSelectionChange) {
      onSelectionChange(!isSelected)
      return
    }

    if (onTap) {
      onTap()
    } else {
      setExpanded(!expanded)
    }
  }

  /**
   * Handle checkbox change
   */
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (onSelectionChange) {
      onSelectionChange(event.target.checked)
    }
  }

  // Show first N fields when collapsed, all when expanded
  const visibleFields = expanded ? fields : fields.slice(0, collapsedFieldCount)
  const hasMoreFields = fields.length > collapsedFieldCount

  /**
   * Get color for secondary action
   */
  const getSecondaryActionColor = (color?: string) => {
    switch (color) {
      case 'success':
        return 'success.main'
      case 'warning':
        return 'warning.main'
      case 'info':
        return 'info.main'
      case 'secondary':
        return 'secondary.main'
      default:
        return 'primary.main'
    }
  }

  return (
    <Box sx={{ position: 'relative', mb: 1 }}>
      {/* Secondary action buttons (revealed on swipe right) - positioned on the left */}
      {secondaryActions && secondaryActions.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            gap: 1,
            pl: 1,
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          {secondaryActions.map((action) => {
            const IconComponent = iconMap[action.icon] || Star
            return (
              <IconButton
                key={action.id}
                onClick={action.onClick}
                sx={{
                  bgcolor: getSecondaryActionColor(action.color),
                  color: 'white',
                  width: 40,
                  height: 40,
                  '&:hover': { opacity: 0.9 },
                }}
                aria-label={action.label}
              >
                <IconComponent size={20} />
              </IconButton>
            )
          })}
        </Box>
      )}

      {/* Primary action buttons (revealed on swipe left) - positioned on the right */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          display: 'flex',
          gap: 1,
          pr: 1,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        {onEdit && (
          <IconButton
            onClick={onEdit}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 40,
              height: 40,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
            aria-label="Edit"
          >
            <Edit size={20} />
          </IconButton>
        )}
        {onDelete && (
          <IconButton
            onClick={onDelete}
            sx={{
              bgcolor: 'error.main',
              color: 'white',
              width: 40,
              height: 40,
              '&:hover': { bgcolor: 'error.dark' },
            }}
            aria-label="Delete"
          >
            <Trash2 size={20} />
          </IconButton>
        )}
      </Box>

      {/* Card (swipeable) */}
      <Card
        {...swipeHandlers}
        onClick={handleCardTap}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="article"
        sx={{
          position: 'relative',
          transform: `translateX(${swipeOffset}px)`,
          transition:
            swipeOffset === 0 ||
            swipeOffset === -primaryActionWidth ||
            swipeOffset === secondaryActionWidth
              ? 'transform 0.3s ease'
              : 'none',
          cursor: 'pointer',
          '&:active': {
            bgcolor: 'action.hover',
          },
          ...(selectionMode && isSelected && {
            bgcolor: 'action.selected',
            borderColor: 'primary.main',
          }),
        }}
      >
        <CardContent>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {/* Selection checkbox */}
            {selectionMode && (
              <Checkbox
                checked={isSelected}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                sx={{ mr: 1, p: 0.5 }}
              />
            )}

            {avatar && (
              <Avatar
                src={typeof avatar === 'string' ? avatar : undefined}
                sx={{ mr: 2, width: 40, height: 40 }}
              >
                {typeof avatar !== 'string' && avatar}
              </Avatar>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {primaryText}
              </Typography>
              {secondaryText && (
                <Typography variant="body2" color="text.secondary">
                  {secondaryText}
                </Typography>
              )}
            </Box>
            {status && (
              <Chip
                label={status.label}
                color={status.color}
                size="small"
                sx={{ ml: 1 }}
              />
            )}
            {!selectionMode && (
              <IconButton
                size="small"
                sx={{ ml: 1 }}
                aria-label="Toggle details"
              >
                {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </IconButton>
            )}
          </Box>

          {/* Data fields */}
          {visibleFields.map((field, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.5,
                borderBottom: index < visibleFields.length - 1 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {field.label}
              </Typography>
              <Typography variant="body2" component="div" fontWeight={500}>
                {field.value}
              </Typography>
            </Box>
          ))}

          {/* Show more indicator */}
          {!expanded && hasMoreFields && !selectionMode && (
            <Typography
              variant="caption"
              color="primary"
              sx={{ display: 'block', textAlign: 'center', mt: 1 }}
            >
              Tap to see {fields.length - collapsedFieldCount} more fields
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
